/**
 * Avisos antes de imprimir. Nunca bloqueiam: a decisão é do PCP. Aparecem
 * na tela, acima das folhas afetadas, e nunca no papel.
 *
 * Cada aviso diz quais setores afeta (`setores`) e, quando é de uma célula,
 * qual (`celulas`: { ambiente, coluna }) para a tela contornar.
 */
import { ehDiaUtil, hojeIso } from "../capacidade/calendario";
import { CAMPO_ENTREGA, IDS_DOS_SETORES, SETORES, TIPO_COLUNA as T, setorPorId } from "./setores";
import { lerItem } from "./celulas";
import { datasDoCampo } from "./lotes";
import { diaDaSemanaPorExtenso, formatarData, formatarDataComDia } from "./datas";
import { MAXIMO_DE_AMBIENTES_POR_FOLHA, loteCabeNumaFolha, setorSeAplica } from "./folhas";

export const CODIGO_AVISO = Object.freeze({
  EXPEDICAO_DEPOIS_DA_ENTREGA: "expedicao_depois_da_entrega",
  ORDEM_DOS_LIMITES: "ordem_dos_limites",
  LIMITE_EM_DIA_NAO_UTIL: "limite_em_dia_nao_util",
  LIMITE_VAZIO: "limite_vazio",
  LIMITE_VENCIDO: "limite_vencido",
  LIMITES_DIFERENTES: "limites_diferentes",
  ITEM_SEM_DESCRICAO: "item_sem_descricao",
  VARIOS_CLIENTES: "varios_clientes",
  OTIMIZACAO_DIFERENTE: "otimizacao_diferente",
  REOTIMIZADO: "reotimizado",
  LOTE_ACIMA_DO_LIMITE: "lote_acima_do_limite",
});
const C = CODIGO_AVISO;

// Compras ≤ Máquinas ≤ Marcenaria ≤ Acabamento ≤ Expedição. Terceiros corre
// em paralelo e fica fora; Expedição × Entrega tem aviso próprio.
const CADEIA_DOS_LIMITES = ["compras", "maquinas", "marcenaria", "acabamento", "expedicao"];

function nomeDoLimite(setor) {
  return setor.rotuloLimite.replace(/^Limite/, "limite");
}

function chaveDoAmbiente(ambiente) {
  return `${ambiente.projeto}|${ambiente.nome}`;
}

function semRepetir(avisos) {
  const vistos = new Map();
  avisos.forEach((aviso) => {
    const existente = vistos.get(aviso.mensagem);
    if (existente) existente.celulas.push(...aviso.celulas);
    else vistos.set(aviso.mensagem, { ...aviso, celulas: [...aviso.celulas] });
  });
  return [...vistos.values()];
}

function aviso(codigo, setores, mensagem, celulas = []) {
  return { codigo, setores, mensagem, celulas };
}

function expedicaoDepoisDaEntrega(lote) {
  const expedicao = setorPorId("expedicao");
  return lote.ambientes.flatMap((ambiente) => {
    const saida = ambiente.linha[expedicao.campoLimite];
    const entrega = ambiente.linha[CAMPO_ENTREGA];
    if (!saida || !entrega || saida <= entrega) return [];
    return [aviso(
      C.EXPEDICAO_DEPOIS_DA_ENTREGA,
      ["expedicao"],
      `Lote ${lote.rotulo}: Expedição (${formatarDataComDia(saida)}) depois da entrega no cliente (${formatarDataComDia(entrega)})`,
      [{ ambiente: chaveDoAmbiente(ambiente), coluna: "limite" }],
    )];
  });
}

function ordemDosLimites(lote) {
  return lote.ambientes.flatMap((ambiente) => {
    const definidos = CADEIA_DOS_LIMITES
      .map(setorPorId)
      .map((setor) => ({ setor, data: ambiente.linha[setor.campoLimite] }))
      .filter(({ data }) => data);
    return definidos.slice(1).flatMap((atual, i) => {
      const anterior = definidos[i];
      if (anterior.data <= atual.data) return [];
      return [aviso(
        C.ORDEM_DOS_LIMITES,
        [anterior.setor.id, atual.setor.id],
        `Lote ${lote.rotulo}: ${anterior.setor.rotuloLimite} (${formatarDataComDia(anterior.data)}) depois do ${nomeDoLimite(atual.setor)} (${formatarDataComDia(atual.data)})`,
      )];
    });
  });
}

// "num domingo (25/10)" ou "num feriado (12/10, segunda)".
function descreverDiaNaoUtil(iso) {
  const dia = diaDaSemanaPorExtenso(iso);
  if (dia === "sábado" || dia === "domingo") return `num ${dia} (${formatarData(iso).slice(0, 5)})`;
  return `num feriado (${formatarDataComDia(iso)})`;
}

function datasDoSetor(lote, setor) {
  return datasDoCampo(lote, setor.campoLimite);
}

function limitesEmDiaNaoUtil(lote, setores, naoUteis) {
  const porSetor = setores.flatMap((setor) => datasDoSetor(lote, setor)
    .filter((data) => !ehDiaUtil(data, naoUteis))
    .map((data) => aviso(
      C.LIMITE_EM_DIA_NAO_UTIL,
      [setor.id],
      `Lote ${lote.rotulo}: ${setor.rotuloLimite} cai ${descreverDiaNaoUtil(data)}`,
    )));
  const entregas = datasDoCampo(lote, CAMPO_ENTREGA)
    .filter((data) => !ehDiaUtil(data, naoUteis))
    .map((data) => aviso(
      C.LIMITE_EM_DIA_NAO_UTIL,
      IDS_DOS_SETORES,
      `Lote ${lote.rotulo}: Entrega no cliente cai ${descreverDiaNaoUtil(data)}`,
    ));
  return [...porSetor, ...entregas];
}

function limitesVazios(lote, setores) {
  if (!lote.emAberto) return [];
  return setores
    .filter((setor) => lote.ambientes.some((ambiente) => !ambiente.linha[setor.campoLimite]))
    .map((setor) => aviso(
      C.LIMITE_VAZIO,
      [setor.id],
      `Lote ${lote.rotulo}: sem data no ${nomeDoLimite(setor)}. A folha sai com o campo em branco`,
    ));
}

function limitesVencidos(lote, setores, hoje) {
  if (!lote.emAberto || !hoje) return [];
  return setores.flatMap((setor) => {
    const maisCedo = datasDoSetor(lote, setor)[0];
    if (!maisCedo || maisCedo >= hoje) return [];
    return [aviso(
      C.LIMITE_VENCIDO,
      [setor.id],
      `Lote ${lote.rotulo}: ${setor.rotuloLimite} (${formatarDataComDia(maisCedo)}) já passou`,
    )];
  });
}

function limitesDiferentes(lote, setores) {
  return setores.flatMap((setor) => {
    const datas = datasDoSetor(lote, setor);
    if (datas.length < 2) return [];
    return [aviso(
      C.LIMITES_DIFERENTES,
      [setor.id],
      `Lote ${lote.rotulo}: ${setor.rotuloLimite} diferente entre os ambientes (${datas.map(formatarDataComDia).join("; ")}). O cartão mostra a mais cedo`,
    )];
  });
}

function itensSemDescricao(lote, setores) {
  return setores.flatMap((setor) => setor.colunas
    .filter((coluna) => coluna.tipo === T.ITEM_DESCRITO)
    .flatMap((coluna) => lote.ambientes
      .filter((ambiente) => {
        const item = lerItem(ambiente.linha[coluna.campo], ambiente.linha[coluna.campoDescricao]);
        return item.tipo === "qtd" && !item.descricao;
      })
      .map((ambiente) => aviso(
        C.ITEM_SEM_DESCRICAO,
        [setor.id],
        `Lote ${lote.rotulo}: ${coluna.rotulo} do ambiente ${ambiente.nome} sem descrição`,
        [{ ambiente: chaveDoAmbiente(ambiente), coluna: coluna.id }],
      ))));
}

function variosClientes(lote) {
  if (lote.clientes.length < 2) return [];
  return [aviso(
    C.VARIOS_CLIENTES,
    IDS_DOS_SETORES,
    `Lote ${lote.rotulo} tem ambientes de ${lote.clientes.length} clientes: ${lote.clientes.join(", ")}`,
  )];
}

function otimizacaoDiferente(lote) {
  const semData = lote.ambientes.filter((ambiente) => !ambiente.otimizacao).length;
  const avisos = [];
  if (lote.otimizacoes.length > 1) {
    avisos.push(aviso(
      C.OTIMIZACAO_DIFERENTE,
      IDS_DOS_SETORES,
      `Lote ${lote.rotulo}: ambientes otimizados em datas diferentes (${lote.otimizacoes.map(formatarDataComDia).join("; ")}). A folha mostra a mais recente`,
    ));
  }
  if (semData > 0) {
    avisos.push(aviso(
      C.OTIMIZACAO_DIFERENTE,
      IDS_DOS_SETORES,
      `Lote ${lote.rotulo}: ${semData === 1 ? "1 ambiente está" : `${semData} ambientes estão`} sem data de otimização`,
    ));
  }
  return avisos;
}

function acimaDoLimite(lote) {
  if (loteCabeNumaFolha(lote)) return [];
  return [aviso(
    C.LOTE_ACIMA_DO_LIMITE,
    IDS_DOS_SETORES,
    `Lote ${lote.rotulo} tem ${lote.ambientes.length} ambientes: o limite é ${MAXIMO_DE_AMBIENTES_POR_FOLHA} por folha. `
      + "Divida o lote na planilha para imprimir os apontamentos dos setores",
  )];
}

function reotimizado(lote, selo) {
  if (selo?.tipo !== "reotimizado") return [];
  const impressoEm = hojeIso(new Date(selo.em));
  return [aviso(
    C.REOTIMIZADO,
    IDS_DOS_SETORES,
    `Lote ${lote.rotulo} foi reotimizado em ${formatarData(lote.otimizacao).slice(0, 5)}. A impressão anterior (${formatarData(impressoEm).slice(0, 5)}) está desatualizada`,
  )];
}

/**
 * @param opcoes.selo selo do lote (impressoes.seloDoLote), para o aviso de reotimizado.
 * @param opcoes.hoje "AAAA-MM-DD"; sem ele, o aviso de limite vencido não roda.
 * @param opcoes.naoUteis Set de "AAAA-MM-DD" (Configurações > Calendário).
 * @param opcoes.setores ids dos setores que vão sair; padrão: todos.
 */
export function validarLote(lote, { hoje = null, naoUteis = new Set(), setores = IDS_DOS_SETORES, selo = null } = {}) {
  const escolhidos = SETORES.filter((setor) => setores.includes(setor.id) && setorSeAplica(setor, lote));
  return semRepetir([
    ...acimaDoLimite(lote),
    ...reotimizado(lote, selo),
    ...variosClientes(lote),
    ...otimizacaoDiferente(lote),
    ...expedicaoDepoisDaEntrega(lote),
    ...ordemDosLimites(lote),
    ...limitesDiferentes(lote, escolhidos),
    ...limitesEmDiaNaoUtil(lote, escolhidos, naoUteis),
    ...limitesVazios(lote, escolhidos),
    ...limitesVencidos(lote, escolhidos, hoje),
    ...itensSemDescricao(lote, escolhidos),
  ]);
}
